import { Text, Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, FormControl, FormLabel, ModalFooter, HStack, Spinner, Select } from '@chakra-ui/react'
import React from 'react'
import { ErrorMessage } from '../components/ErrorMessage';
import { useOperation } from '../components/useOperation';
import { ICourse } from '@/shared/ICourse';
import { NewExpertDialog } from '../dashboard/NewExpertDialog';
import { CourseExpertStorage } from '../services/CourseExpertStorage';
import { IExpert } from '@/shared/IExpert';
import { CourseStorageService } from '../services/CourseStorageService';

export const AssignToExpertDialog = (props: {
  course: ICourse;
  onClose: () => void;
  onExpertChanged?: (course: ICourse) => void;
}) => {

  const [expertId, setExpertId] = React.useState(props.course.expertId);

  const [onSave, errorMessage, isProcessing] = useOperation(async () => {
    if (props.course) {
      const updated = await CourseStorageService.assignToExpert(props.course.id, expertId);
      props.onExpertChanged?.(updated);
      props.onClose();
    }
  }, [expertId]);

  const isDisabled = isProcessing || props.course.expertId === expertId;

  const initialRef = React.useRef(null)

  const [experts, setExperts] = React.useState<IExpert[]>([]);

  const [isNewExpertDialogOpen, setIsNewExportDialogOpen] = React.useState(false);

  const [loadErrorMessage, setLoadErrorMessage] = React.useState<any>();

  const loadExperts = async () => {
    setLoadErrorMessage(undefined);
    try {
      const loaded = await CourseExpertStorage.getExperts();
      const reviewers = loaded.filter(expert => expert.role !== 'staff');
      setExperts(reviewers);
    } catch (err) {
      setLoadErrorMessage(err);
    }
  };

  React.useEffect(() => {
    loadExperts();
  }, []);

  const onAddNewExpert = async (expert: IExpert, invite: boolean) => {
    const created = await CourseExpertStorage.createExpert(expert, invite);
    setExperts(old => [...old, created]);
    setExpertId(created.id);
  }

  return (
    <Modal
      initialFocusRef={initialRef}
      finalFocusRef={initialRef}
      size={'xl'}
      isOpen
      onClose={props.onClose}
      preserveScrollBarGap
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Grant an External Reviewer access to this course</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <ErrorMessage error={loadErrorMessage} />
          <ErrorMessage error={errorMessage} />

          <Text>After the person accepts this invitation, he/she will be able to access this course submission to make a review.</Text>

          <HStack spacing={4} mt='4'>
            <FormControl key='expert' mt='4'>
              <FormLabel>Assigned External Reviewer</FormLabel>
              <Select isRequired value={expertId || ''} onChange={e => setExpertId(e.target.value)}>
                <option value=''>-</option>
                {experts.map(expert => (
                  <option key={expert.id} value={expert.id}>
                    {`${expert.name} (${expert.email})`}
                  </option>
                ))}
              </Select>
              <Text mt='1' fontSize='small' color='fg.subtle'>Please select an External Reviewer to review this course.</Text>
            </FormControl>
            <Button onClick={() => setIsNewExportDialogOpen(true)} variant='outline'>New...</Button>
          </HStack>

          {isNewExpertDialogOpen && <NewExpertDialog onClose={() => setIsNewExportDialogOpen(false)} onAddNewExpert={onAddNewExpert} />}

        </ModalBody>

        <ModalFooter>
          <Button variant='solid' isDisabled={isDisabled} mr={4} onClick={onSave}>
            <HStack spacing={2}>{isProcessing && <Spinner />}<Text>{isProcessing ? "Assigning..." : "Assign"}</Text></HStack>
          </Button>
          <Button variant='outline' onClick={props.onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}